<template>
	<div>
		<div class="course-table__filters">
			<div class="course-table__filters__item">
				<button @click="toggleSearch()" class="course-table__text-toggle">
          <span class="sr-only">Show Text Search</span>
        </button>
				<input placeholder="Search by Keyword" class="course-table__text-input" type="search" title="Course Search" v-model="searchString"/>
			</div>
			<div tabindex="-1" @keydown.esc="clearPanels" @mousedown="stopProp" class="course-table__filters__item course-table__filters__item--select">
				<button @mousedown="stopProp" class="course-table__select-toggle" :class="{'course-table__select-toggle--active': showSelectOpen}" @click="toggleShowSelect">Show</button>
				<div @click="stopProp" v-if="showSelectOpen" class="course-table__filters__item__collection">
					<div v-for="(item, index) in toggleableColumns" :key="index">
						<label tabindex="0" :ref="setShowItemRef" @keyup.enter="showItemClick(index)">
							<input tabindex="-1" type="checkbox" v-model="item.isChecked"/>
							<span v-text="item.name"></span>
						</label>
					</div>
					<button ref="showToggle" class="course-table__filters__panel-button" @click="toggleShowSelect">Close Menu</button>
				</div>
			</div>
			<div tabindex="-1" @keydown.esc="clearPanels" @mousedown="stopProp" class="course-table__filters__item course-table__filters__item--filters course-table__filters__item--select">
				<button @mousedown="stopProp" class="course-table__select-toggle" :class="{'course-table__select-toggle--active': showFiltersOpen}" @click="toggleFilterSelect">Filters</button>
				<div v-if="showFiltersOpen" class="course-table__filters__item__collection">
					<div v-for="(item, key) in filters" :key="key" class="course-table__filters__item__collection__filter-group">
						<div class="course-table__filters__item__collection__filter-group__interior">
							<button :ref="setFilterItemRef" class="course-table__filters__item__panel__toggle" :class="{'course-table__filters__item__panel__toggle--active': showPanel(key)}" @click="toggleKeyPanel(key)" v-text="key"></button>
							<div class="course-table__filters__item__panel" :class="{'course-table__filters__item__panel--show': showPanel(key)}">
								<div v-for="(subItem, index) in item" :key="index" class="course-table__filters__item__collection__filter-group__box-cell">
									<label tabindex="0" :ref="setFilterItemRef" @keyup.enter="filterClick">
										<input tabindex="-1" :ref="setFilterItemRef" type="checkbox" v-model="subItem.isChecked"/>
										<span v-if="key === 'Location'" v-text="translateForDisplay(subItem.value)"></span>
										<span v-else-if="key === 'Session'" v-text="translateForDisplay(subItem.value, true)"></span>
										<span v-else v-text="subItem.value"></span>
									</label>
								</div>
							</div>
						</div>
					</div>
					<button ref="filterToggle" class="course-table__filters__panel-button" @click="toggleFilterSelect">Close Menu</button>
				</div>
			</div>
		</div>
		<input title="Course Search" class="course-table__text-input course-table__text-input--mobile" :class="{'course-table__text-input--mobile--active': searchVisible}" type="search" v-model="searchString"/>
		<div v-if="Object.keys(activeFilterGroups).length" class="course-table__badges">
			<div v-for="(item, key) in activeFilterGroups" :key="key">
				<button @click="deactivateFilter(key, subItem)" v-for="(subItem, subIndex) in item" :key="subIndex" class="course-table__badges__item">
					<span v-if="key === 'Location'" v-text="translateForDisplay(subItem)"></span>
					<span v-else-if="key === 'Session'" v-text="translateForDisplay(subItem, true)"></span>
					<span v-else v-text="subItem"></span>
					<span class="course-table__badges__item__remove"><span class="sr-only">Remove Filter</span></span>
				</button>
			</div>
			<button class="course-table__badges__clear" @click="clearFilters()">Clear Filters</button>
		</div>
		<div v-if="qualifyingCourses.length" class="course-table__v-frame" :class="scrollClass">
			<div class="course-table__v" ref="tableBase" @scroll="setScrollClass()">
				<div class="course-table__v__table" ref="tableInterior">
					<thead>
					<th v-for="(item, index) in visibleColumnHeads" :key="index" v-text="item"></th>
					</thead>
					<tbody>
					<a v-for="(item, index) in qualifyingCourses" :key="index" :href="item.url" :target="_blank">
						<div v-if="columnsObject['Subject'].isChecked" v-text="item['Subject']"></div>
						<div v-if="columnsObject['Catalog Number'].isChecked" v-text="item.catalogNumber"></div>
						<div v-if="columnsObject['Course Title'].isChecked" v-text="item.title"></div>
						<div v-if="columnsObject['Times'].isChecked" v-text="arrayToString(item['Times'])"></div>
						<div v-if="columnsObject['Session'].isChecked" v-text="arrayToString(item['Session'], true)"></div>
						<div v-if="columnsObject['GEP'].isChecked" v-text="arrayToString(item['GEP'], false, true)"></div>
						<div v-if="columnsObject['Location'].isChecked" v-text="translateForDisplay(item['Location'])"></div>
						<div v-if="columnsObject['Format'].isChecked" v-text="arrayToString(item['Format'])"></div>
						<div v-if="columnsObject['Start Time'].isChecked" v-text="item['Start Time']"></div>
						<div v-if="columnsObject['End Time'].isChecked" v-text="item['End Time']"></div>
						<div v-if="columnsObject['Days'].isChecked" v-text="arrayToString(item['Days'])"></div>
					</a>
					</tbody>
				</div>
			</div>
		</div>
		<div v-else class="course-table__error">No courses matched your search. You can try removing terms or filters, or start again.</div>
	</div>
</template>

<script>
import { toRefs, reactive, ref, computed, nextTick, onBeforeUpdate } from 'vue';
export default {
	props: {
		courses: {
			type: Array,
			required: true
		},
		filters: {
			type: Object,
			required: true
		},
		searchString: {
			type: String,
			required: true
		}
	},
	setup(props, { emit }) {
		const {
			filters,
			courses,
			searchString
		} = toRefs(props);

		const tableBase = ref(null);
		const tableInterior = ref(null);
		const filterToggle = ref(null);
		const showToggle = ref(null);
		const scrollClass = ref('');
		const searchVisible = ref(false);
		const openKeyPanels = reactive([]);

		let showItemRefs = [];
		function setShowItemRef(el) {
			if (el) {
				showItemRefs.push(el)
			}
		};
		onBeforeUpdate(() => {
			showItemRefs = [];
		});

		let filterItemRefs = [];
		function setFilterItemRef(el) {
			if (el) {
				filterItemRefs.push(el)
			}
		};
		onBeforeUpdate(() => {
			filterItemRefs = [];
		});

		let columnsObject = ref({
			'Subject' : {
				isChecked: true,
				name: 'Subject'
			},
			'Catalog Number' : {
				isChecked: true,
				name: 'Catalog Number'
			},
			'Course Title' : {
				isChecked: true,
				name: 'Course Title'
			},
			'Times' : {
				isChecked: false,
				name: 'Times'
			},
			'Session' : {
				isChecked: true,
				name: 'Session'
			},
			'GEP' : {
				isChecked: false,
				name: 'GEP'
			},
			'Location' : {
				isChecked: false,
				name: 'Location'
			},
			'Format' : {
				isChecked: true,
				name: 'Format'
			},
			'Start Time' : {
				isChecked: false,
				name: 'Start TIme'
			},
			'End Time' : {
				isChecked: false,
				name: 'End Time'
			},
			'Days' : {
				isChecked: true,
				name: 'Days'
			}
		});

		let showSelectOpen = ref(false);
		let showFiltersOpen = ref(false);

		let visibleColumnHeads = computed(function() {
			setScrollClass();
			let arrayToReturn = [];
			let co = columnsObject.value;
			for (let key in co) {
				if (co[key].isChecked) {
					arrayToReturn.push(key);
				}
			}
			return arrayToReturn;
		});

		let toggleableColumns = computed(function() {
			let arrayToReturn = [];
			let co = columnsObject.value;
			for (let key in co) {
				if (key !== 'Subject' && key !== 'Catalog Number' && key !== 'Course Title') {
					arrayToReturn.push(co[key]);
				}
			}
			return arrayToReturn;
		});

		let activeFilterGroups = computed(function() {
			let objectToReturn = {};
			let fv = filters.value;
			for (let filterKey in fv) {
				for ( let i = 0; i < fv[filterKey].length; i++) {
					if ( fv[filterKey][i].isChecked ) {
						if (!objectToReturn[filterKey]) {
							objectToReturn[filterKey] = [];
						}
						objectToReturn[filterKey].push(fv[filterKey][i].value);
					}
				}
			}
			return objectToReturn;
		});

		let qualifyingCourses = computed(function() {
			let arrayToReturn = [];
			let courseSource = courses.value;
			let trimmedSearch = searchString.value.toLowerCase();
			let numberActiveFilters = Object.keys(activeFilterGroups.value).length;
			let hasActiveFilters = (numberActiveFilters > 0);
			for (let i = 0; i < courseSource.length; i++) {
				if (trimmedSearch.length) {
					let fullString = (courseSource[i].title + ' ' + courseSource[i]['Subject']).toLowerCase();
					let stringMatch = checkStringMatch(trimmedSearch, fullString);
					if (hasActiveFilters) {
						//item must pass search and filters
						if (stringMatch) {
							let passesFilter = checkFilterPass(courseSource[i], numberActiveFilters);
							if (passesFilter) {
								arrayToReturn.push(courseSource[i]);
							}
						}
					} else {
						//pass search only
						if (stringMatch) {
							arrayToReturn.push(courseSource[i]);
						}
					}
				} else {
					if (hasActiveFilters) {
						//items through if pass filter only
						let passesFilter = checkFilterPass(courseSource[i], numberActiveFilters);
						if (passesFilter) {
							arrayToReturn.push(courseSource[i]);
						}
					} else {
						//all items put through without filter
						arrayToReturn.push(courseSource[i]);
					}
				}
			}
			return arrayToReturn;
		});

		document.body.classList.add('ct-mount');
		document.documentElement.addEventListener('mousedown', function() {
			clearPanels();
		});
		window.addEventListener('resize', function() {
			setScrollClass();
		});

		document.addEventListener('focusin', function(e) {
			if (showSelectOpen.value || showFiltersOpen.value) {
				let srcElement = e.srcElement;
				if (srcElement) {
					if (showSelectOpen.value) {
						if (showItemRefs.indexOf(srcElement) === -1) {
							if (srcElement !== showToggle.value) {
								let firstElement = showItemRefs[0];
								if (firstElement) {
									firstElement.focus();
								}
							}
						}
					}
					if (showFiltersOpen.value) {
						if (filterItemRefs.indexOf(srcElement) === -1) {
							if (srcElement !== filterToggle.value) {
								let firstElement = filterItemRefs[0];
								if (firstElement) {
									firstElement.focus();
								}
							}
						}
					}
				}
			}
		});

		function clearPanels() {
			showSelectOpen.value = false;
			showFiltersOpen.value = false;
		}

		function setScrollClass() {
			nextTick(() => {
				if (tableBase.value && tableInterior.value) {
					if (tableBase.value.clientWidth < tableInterior.value.clientWidth ) {
						let overflowAmount = tableInterior.value.clientWidth - tableBase.value.clientWidth;
						if (tableBase.value.scrollLeft >= overflowAmount) {
							scrollClass.value = '';
						} else {
							scrollClass.value = 'course-table__v-frame--scrollable';
						}
					} else {
						scrollClass.value = '';
					}
				} else {
					scrollClass.value = '';
				}
			});
		}

		function checkFilterPass(singleCourse, numberActiveFilters) {
			let afg = activeFilterGroups.value;
			let numberPassed = 0;
			for (let key in afg) {
				let singleCourseSlot = singleCourse[key];
				if (Array.isArray(singleCourseSlot)) {
					for (let i = 0; i < singleCourseSlot.length; i++) {
						if (afg[key].indexOf(singleCourseSlot[i]) !== -1) {
							numberPassed++;
							break;
						}
					}
				} else {
					if (afg[key].indexOf(singleCourseSlot) !== -1) {
						numberPassed++;
					}
				}
			}
			if (numberPassed === numberActiveFilters) {
				return true;
			} else {
				return false;
			}
		}

		function deactivateFilter(key, name) {
			let fv = filters.value;
			let filterGroup = fv[key];
			if (filterGroup) {
				for (let i = 0; i < filterGroup.length; i++) {
					if (filterGroup[i].value === name) {
						filterGroup[i].isChecked = false;
						break;
					}
				}
			}
		}

		function checkStringMatch(searchString, fullString) {
			let searchArray = searchString.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g,'').split(' ');
			for (let i = 0; i < searchArray.length; i++) {
				if (searchArray[i] && fullString.indexOf(searchArray[i]) !== -1) {
					return true;
				} else {
					return false;
				}
			}
			return false;
		}

		function stopProp(e) {
			e.stopImmediatePropagation();
		}

		function toggleShowSelect(e) {
			e.stopImmediatePropagation();
			showFiltersOpen.value = false;
			if (showSelectOpen.value) {
				showSelectOpen.value = false;
			} else {
				showSelectOpen.value = true;
			}
		}

		function clearFilters() {
			let fv = filters.value;
			for (let key in fv) {
				let filterGroup = fv[key];
				if (filterGroup) {
					for (let i = 0; i < filterGroup.length; i++) {
						filterGroup[i].isChecked = false;
					}
				}
			}
		}

		function translateForDisplay(inputString, isSession = false) {
			if (isSession) {
				if (inputString.length) {
					let stringParts = inputString.split('W');
					let followString = ' Weeks ';
					if (stringParts[0] === '12') {
						followString = ' Weeks';
					}
					let stringToReturn = stringParts[0] + followString;
					if (stringParts[1] === '1') {
						stringToReturn += 'Session I';
					} else if (stringParts[1] === '2') {
						stringToReturn += 'Session II';
					} else if (stringParts[1] === '3') {
						stringToReturn += 'Session III';
					} else if (stringParts[1] === '4') {
						stringToReturn += 'Session IV';
					}
					return stringToReturn;
				} else {
					return '';
				}
			} else {
				if (inputString === 'MAIN') {
					return 'Main Campus';
				} else if (inputString === 'SHGR') {
					return 'Shady Grove';
				} else if (inputString === 'OFF') {
					return 'Off Campus';
				}
			}
		}

		function toggleFilterSelect(e) {
			e.stopImmediatePropagation();
			showSelectOpen.value = false;
			if (showFiltersOpen.value) {
				showFiltersOpen.value = false;
			} else {
				showFiltersOpen.value = true;
			}
		}

		function toggleSearch(e) {
			if (searchVisible.value) {
				searchVisible.value = false;
			} else {
				searchVisible.value = true;
			}
		}

		function arrayToString(inputArray, isSession = false, isGEP = false) {
			let stringToReturn = '';
			for (let i = 0; i < inputArray.length; i++) {
				if (isSession) {
					stringToReturn += translateForDisplay(inputArray[i], true)
				} else if (isGEP) {
					if (inputArray[i] === 'STEM') {
						stringToReturn += 'STEM';
					} else {
						let regExp = /\(([^)]+)\)/;
						let matches = regExp.exec(inputArray[i]);
						if (matches) {
							stringToReturn += matches[1];
						}
					}
				} else {
					stringToReturn += inputArray[i];
				}
				if (i < inputArray.length - 1 ) {
					stringToReturn += ', ';
				}
			}
			return stringToReturn;
		}

		function toggleKeyPanel(key) {
			let keyIndex = openKeyPanels.indexOf(key);
			if (keyIndex !== -1) {
				openKeyPanels.splice(keyIndex, 1);
			} else {
				openKeyPanels.push(key);
			}
		}

		function showPanel(key) {
			if (openKeyPanels.indexOf(key) !== -1) {
				return true;
			}
			return false;
		}

		function showItemClick(index) {
			if (showItemRefs && showItemRefs[index]) {
				showItemRefs[index].click();
			}
		}

		function filterClick(e) {
			if (e.srcElement) {
				let element = e.srcElement;
				let refIndex = filterItemRefs.indexOf(element);
				filterItemRefs[refIndex].click();
			}
		}

		return {
			arrayToString,
			toggleableColumns,
			showSelectOpen,
			showFiltersOpen,
			toggleShowSelect,
			toggleFilterSelect,
			toggleSearch,
			stopProp,
			columnsObject,
			qualifyingCourses,
			visibleColumnHeads,
			toggleableColumns,
			searchString,
			searchVisible,
			translateForDisplay,
			filters,
			activeFilterGroups,
			deactivateFilter,
			clearFilters,
			scrollClass,
			tableBase,
			tableInterior,
			openKeyPanels,
			toggleKeyPanel,
			showPanel,
			setScrollClass,
			setShowItemRef,
			setFilterItemRef,
			showItemClick,
			filterClick,
			filterToggle,
			showToggle,
			clearPanels
		};
	}
}
</script>